import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { useFloorPlanStore } from '../store/useFloorPlanStore'
import { FLOOR_PLAN_USE_API } from '../config'
import FloorPlanAPI from '../api/FloorPlanAPI'
import { floorPlanToStore, serializePlan } from '../api/floorPlanMapper'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/**
 * Explicit, on-demand persistence for the floor plan. Editing the plan only
 * touches the local (offline-first) store, nothing is sent to the backend
 * until `save()` is called, e.g. from the Save button. This is deliberate: the
 * backend persists the whole plan in one PUT, so saving on every edit would both
 * hammer the API and reject valid in-progress states.
 */
export function useFloorPlanSave() {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const rooms = useFloorPlanStore(s => s.rooms)
  const tables = useFloorPlanStore(s => s.tables)
  const savedSnapshot = useFloorPlanStore(s => s.savedSnapshot)

  const [status, setStatus] = useState<SaveStatus>('idle')
  // Guards against overlapping saves (double-click / rapid clicks).
  const savingRef = useRef(false)

  // Current plan in the exact form a save would PUT.
  const current = useMemo(() => serializePlan(rooms, tables), [rooms, tables])

  // Dirty once a baseline exists and the plan has diverged from it. Before the
  // first hydration/save the baseline is null and we treat the plan as clean.
  const isDirty = savedSnapshot !== null && current !== savedSnapshot

  const save = useCallback(async () => {
    if (!FLOOR_PLAN_USE_API || savingRef.current) return
    savingRef.current = true
    setStatus('saving')

    const state = useFloorPlanStore.getState()
    const { activeRoomId } = state
    // Room order is preserved by the backend, so remember the active room's
    // position to keep it selected after we adopt the server-assigned ids.
    const sortedRoomIds = [...state.rooms]
      .sort((a, b) => a.order - b.order)
      .map(room => room.id)
    const activeIndex = activeRoomId ? sortedRoomIds.indexOf(activeRoomId) : -1

    const sentSnapshot = current
    try {
      const updated = await FloorPlanAPI.updateFloorPlan(JSON.parse(sentSnapshot))
      const mapped = floorPlanToStore(updated)

      // If the user edited during the request, the response reflects the OLDER
      // plan, don't overwrite the newer local geometry. Just record the version
      // we saved as the baseline; the plan stays dirty so a follow-up save picks
      // up the edits (and adopts ids then).
      const after = useFloorPlanStore.getState()
      const storeUnchanged =
        serializePlan(after.rooms, after.tables) === sentSnapshot

      if (storeUnchanged && mapped.rooms.length > 0) {
        // Adopt the backend's ids/normalised numbers so the next save updates
        // the same rooms/tables instead of recreating them.
        useFloorPlanStore.setState({
          rooms: mapped.rooms,
          tables: mapped.tables,
          activeRoomId:
            mapped.rooms[activeIndex]?.id ?? mapped.rooms[0]?.id ?? null,
          savedSnapshot: serializePlan(mapped.rooms, mapped.tables)
        })
      } else {
        useFloorPlanStore.setState({ savedSnapshot: sentSnapshot })
      }

      setStatus('saved')
      showSnackbar(
        t('FLOOR_PLAN.ALERTS.SAVE_SUCCESS.TITLE'),
        t('FLOOR_PLAN.ALERTS.SAVE_SUCCESS.DESCRIPTION'),
        'success'
      )
    } catch {
      setStatus('error')
      showSnackbar(
        t('FLOOR_PLAN.ALERTS.SYNC_ERROR.TITLE'),
        t('FLOOR_PLAN.ALERTS.SYNC_ERROR.DESCRIPTION'),
        'error'
      )
    } finally {
      savingRef.current = false
    }
  }, [current, showSnackbar, t])

  return { save, status, isDirty, canSave: FLOOR_PLAN_USE_API }
}
